import React, { FC, useState } from 'react';
import { StyleProp, Image, ImageStyle } from 'react-native';
import { fullHeight, fullWidth, scale as initialScale } from '@components/frame';
import { ProgressiveImageTabletMobileProps } from './ProgressiveImageTabletMobile';
import ImageBase64 from './ImageBase64';
import { font as initialFont } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import { ATTACH_ITEMS_ON_TOP_OFF_IMAGE_LEFT, ATTACH_ITEMS_ON_TOP_OFF_IMAGE_TOP, IMAGE_ZOOM_CONSTRAINTS } from '@v2/components/imageHandling/constants';
import { useManageImage } from './hooks/useManageImage';
import Processing from '@v2/components/imageHandling/components/Processing';
import AttachItemsGlass from '@components/common/AttachItemsGlass';
import { useImageViewTransformController } from '@v2/components/imageHandling/components/DrawingCanvas/hooks/useImageViewTransformController';
import ZoomControls from './ZoomControls';

export interface ProgressiveImageWebProps extends Omit<ProgressiveImageTabletMobileProps, 'onImagePress' | 'withNotchTop' | 'onZoomStart'> {
  imageStyle?: StyleProp<ImageStyle>;
  C: ColorFunction;
}

const ProgressiveImageWeb: FC<ProgressiveImageWebProps> = ({
  image,
  containerStyle,
  imageStyle,
  withScalingPanning,
  C,
  withoutAttachItems,
  resizeMode = 'contain',
  scale = initialScale,
  font = initialFont,
  width = fullWidth,
  height = fullHeight,
}) => {
  const [highResLoaded, setHighResLoaded] = useState(false);
  const { angle, highResUri, lowResUri, swapDimensions, imageHeight, imageWidth, shouldDisplayPreviewBase64 } = useManageImage({
    image,
    width,
    height,
  });

  const { containerRef, handleDoubleClick, handleMouseDown, handleMouseMove, handleMouseUp, isDragging, panX, panY, zoom, zoomIn, zoomOut } =
    useImageViewTransformController({ withScalingPanning, rotationAngle: angle, documentId: image?.documentId });

  // if (shouldDisplayPreviewBase64) {
  //   return <ImageBase64 {...{ base64: image.base64, containerStyle, width, height, scale, font, angle }} />;
  // }

  const defaultImageStyle: ImageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  };

  const transformStyle = {
    transform: `translate(${swapDimensions ? panY : panX}px, ${swapDimensions ? panX : panY}px) scale(${zoom})`,
    transformOrigin: 'center center',
    transition: isDragging ? 'none' : 'transform 0.1s ease-out',
  };

  return (
    <div style={{ position: 'relative', width, height, display: 'flex', justifyContent: 'center', alignItems: 'center', ...(containerStyle || {}) }}>
      {!withoutAttachItems && (
        <div
          style={{
            position: 'absolute',
            top: scale(ATTACH_ITEMS_ON_TOP_OFF_IMAGE_TOP),
            left: scale(ATTACH_ITEMS_ON_TOP_OFF_IMAGE_LEFT),
            zIndex: 100000000,
          }}
        >
          <AttachItemsGlass image={image} scale={scale} font={font} />
        </div>
      )}

      {shouldDisplayPreviewBase64 ? (
        <ImageBase64 {...{ base64: image.base64, containerStyle, width, height, scale, font, angle }} />
      ) : (
        <div
          ref={containerRef}
          style={{
            width: imageWidth,
            height: imageHeight,
            overflow: 'hidden',
            cursor: zoom > IMAGE_ZOOM_CONSTRAINTS.min ? (isDragging ? 'grabbing' : 'grab') : 'default',
            position: 'relative',
            touchAction: 'none',
            transform: `rotate(${angle}deg)`,
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
        >
          {/* Low res image - shown initially */}
          {!highResLoaded && <Image source={{ uri: lowResUri }} style={[defaultImageStyle, imageStyle, transformStyle]} resizeMode={resizeMode} />}

          {/* High res image - replaces low res when loaded */}
          <Image
            source={{ uri: highResUri }}
            style={[defaultImageStyle, imageStyle, transformStyle, { opacity: highResLoaded ? 1 : 0 }]}
            onLoad={() => setHighResLoaded(true)}
            resizeMode={resizeMode}
          />
        </div>
      )}

      {withScalingPanning && <ZoomControls C={C} scale={scale} zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} />}

      {image.isProcessing && <Processing scale={scale} font={font} />}
    </div>
  );
};

export default ColorWrapper(ProgressiveImageWeb);
